import { useState } from 'react';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  doc,
  setDoc,
  deleteDoc,
  serverTimestamp,
  writeBatch
} from 'firebase/firestore';
import { useFirebase } from '../context/FirebaseContext';

export const useProjects = () => {
  const { db, currentUser } = useFirebase();
  const [projects, setProjects] = useState([]);

  // Load all projects for current user
  const loadProjects = async () => {
    try {
      if (!currentUser) {
        setProjects([]);
        return [];
      }

      // Filter projects by userId
      const q = query(
        collection(db, 'projects'),
        where('createdBy', '==', currentUser.uid)
      );
      const querySnapshot = await getDocs(q);
      const results = [];

      querySnapshot.forEach((doc) => {
        results.push(doc.data().project);
      });

      setProjects(results);
      return results;
    } catch (error) {
      console.error('Error loading projects:', error);
      throw error;
    }
  };

  // Save new project
  const saveProject = async (project) => {
    try {
      if (!currentUser) {
        throw new Error('User must be logged in to create a project');
      }

      const projectName = project.toLowerCase();
      // Create unique document ID combining userId and project name
      const docId = `${currentUser.uid}_${projectName}`;

      const projectData = {
        project: projectName,
        datum: serverTimestamp(),
        createdBy: currentUser.uid
      };

      // Use userId + project name as document ID to ensure uniqueness per user
      await setDoc(doc(db, 'projects', docId), projectData);

      // Refresh projects list
      await loadProjects();

      return projectName;
    } catch (error) {
      console.error('Error saving project:', error);
      throw error;
    }
  };

  // Delete project (and all related entries and campaigns)
  const deleteProject = async (project) => {
    try {
      if (!currentUser) {
        throw new Error('User must be logged in to delete a project');
      }

      // First delete all entries in this project for this user
      const entriesQuery = query(
        collection(db, 'entries'),
        where('projectId', '==', project),
        where('userId', '==', currentUser.uid)
      );
      const entriesSnapshot = await getDocs(entriesQuery);

      const batch = writeBatch(db);

      entriesSnapshot.forEach((doc) => {
        batch.delete(doc.ref);
      });

      await batch.commit();

      // Then delete all campaigns in this project for this user
      const campaignsQuery = query(
        collection(db, 'campaigns'),
        where('projectId', '==', project),
        where('createdBy', '==', currentUser.uid)
      );
      const campaignsSnapshot = await getDocs(campaignsQuery);

      const campaignBatch = writeBatch(db);

      campaignsSnapshot.forEach((doc) => {
        campaignBatch.delete(doc.ref);
      });

      await campaignBatch.commit();

      // Finally delete the project itself
      const docId = `${currentUser.uid}_${project}`;
      await deleteDoc(doc(db, 'projects', docId));

      // Refresh projects list
      await loadProjects();

      return true;
    } catch (error) {
      console.error('Error deleting project:', error);
      throw error;
    }
  };

  return {
    projects,
    loadProjects,
    saveProject,
    deleteProject
  };
};

export default useProjects;


